import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Email,
  WhatsApp,
  Notifications,
  Visibility,
} from '@mui/icons-material';
import { backofficeApi } from '../../services/backofficeApi';

interface NotificationTemplate {
  id: string;
  notification_type: string;
  channel: string;
  subject?: string;
  content: string;
  variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const NOTIFICATION_TYPES = [
  'USER_REGISTRATION',
  'USER_SIGNUP_REQUEST',
  'USER_SIGNUP_APPROVED',
  'USER_SIGNUP_REJECTED',
  'USER_PASSWORD_CHANGED',
  'USER_PROMOTIONAL',
  'ORDER_PLACED',
  'ORDER_CONFIRMED',
  'ORDER_SHIPPED',
  'ORDER_DELIVERED',
  'ORDER_INVOICED',
  'ORDER_CLOSED',
  'ORDER_CANCELLED',
  'PAYMENT_RECEIVED',
  'PAYMENT_REJECTED',
  'PAYMENT_REMINDER',
  'BACKOFFICE_ORDER_PLACED',
];

const CHANNELS = ['EMAIL', 'WHATSAPP', 'IN_APP'];

const NotificationTemplateManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<NotificationTemplate | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    notification_type: '',
    channel: '',
    subject: '',
    content: '',
    variables: '[]',
    is_active: 'true',
  });

  useEffect(() => {
    loadTemplates();
  }, [page, rowsPerPage]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const params: any = {
        page: page + 1,
        size: rowsPerPage,
      };

      const response = await backofficeApi.get('/backoffice/templates/', { params });
      setTemplates(response.data.templates);
      setTotalCount(response.data.total_count);
    } catch (error: any) {
      setError('Failed to load templates: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template?: NotificationTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        notification_type: template.notification_type,
        channel: template.channel,
        subject: template.subject || '',
        content: template.content,
        variables: JSON.stringify(template.variables, null, 2),
        is_active: template.is_active ? 'true' : 'false',
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        notification_type: '',
        channel: '',
        subject: '',
        content: '',
        variables: '[]',
        is_active: 'true',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
  };

  const handleSave = async () => {
    try {
      const templateData = {
        ...formData,
        variables: JSON.parse(formData.variables),
        is_active: formData.is_active === 'true',
      };

      if (editingTemplate) {
        await backofficeApi.put(`/backoffice/templates/${editingTemplate.id}`, templateData);
      } else {
        await backofficeApi.post('/backoffice/templates/', templateData);
      }

      handleCloseDialog();
      loadTemplates();
    } catch (error: any) {
      setError('Failed to save template: ' + error.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await backofficeApi.delete(`/backoffice/templates/${id}`);
        loadTemplates();
      } catch (error: any) {
        setError('Failed to delete template: ' + error.message);
      }
    }
  };

  const handlePreview = (template: NotificationTemplate) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'email':
        return <Email color="primary" />;
      case 'whatsapp':
        return <WhatsApp color="success" />;
      case 'in_app':
        return <Notifications color="secondary" />;
      default:
        return <Notifications />;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'email':
        return 'primary';
      case 'whatsapp':
        return 'success';
      case 'in_app':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Notification Templates
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage notification templates for different channels
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      {template.notification_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getChannelIcon(template.channel)}
                        <Chip
                          label={template.channel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          color={getChannelColor(template.channel) as any}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {template.subject || 'No Subject'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={template.is_active ? 'Active' : 'Inactive'}
                        color={template.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(template.updated_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handlePreview(template)} size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton onClick={() => handleOpenDialog(template)} size="small">
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(template.id)} size="small" color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            />
          </>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate ? 'Edit Notification Template' : 'Create Notification Template'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Notification Type</InputLabel>
              <Select
                value={formData.notification_type}
                onChange={(e) => setFormData({ ...formData, notification_type: e.target.value })}
                label="Notification Type"
              >
                {NOTIFICATION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                value={formData.channel}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                label="Channel"
                disabled={!!editingTemplate}
              >
                {CHANNELS.map((channel) => (
                  <MenuItem key={channel} value={channel}>
                    {channel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(formData.channel === 'email' || formData.channel === 'in_app') && (
              <TextField
                fullWidth
                label="Subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              />
            )}

            <TextField
              fullWidth
              label="Template Content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              multiline
              rows={8}
              placeholder="Use variables like {{variable_name}} in your template"
            />

            <TextField
              fullWidth
              label="Variables (JSON Array)"
              value={formData.variables}
              onChange={(e) => setFormData({ ...formData, variables: e.target.value })}
              multiline
              rows={4}
              placeholder='["variable_name1", "variable_name2"]'
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                label="Status"
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent>
          {previewTemplate && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {previewTemplate.notification_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} - {previewTemplate.channel.toUpperCase()}
              </Typography>
              {previewTemplate.subject && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Subject:</Typography>
                  <Typography variant="body1">{previewTemplate.subject}</Typography>
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">Content:</Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {previewTemplate.content}
                  </Typography>
                </Paper>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Variables:</Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <pre>{JSON.stringify(previewTemplate.variables, null, 2)}</pre>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationTemplateManagementPage;